"use client";
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createLogger } from '@/utils/logger/devLogger';
import { TextField } from '@/components/form/fields';
import Label from "@/components/form/Label";
import Dropdown from "@/components/form/Dropdown";
import Typography from "@/components/ui/Typography";
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { US_STATES } from '@/utils/constants/stateConstants';
import { useTheme } from '@/context/ThemeContext';
import { 
  loadGoogleMapsApiWithMonitoring, 
  isGoogleMapsApiLoaded,
  setupPlacesWidgetInContainer,
  setupNewPlacesEventHandler,
  type PlacesAutocompleteOptions
} from '@/utils/utilities/googleMaps';

import type { 
  UniversalLocationInputProps, 
  UniversalLocationData, 
  LocationState,
  PlaceData
} from './types';
import { 
  getComponentConfig,
  parseGooglePlaceData,
  validateLocationData,
  formatLocationDisplay,
  isLocationDataComplete,
  mergeLocationData,
  isValidZipCode
} from './utils';

const universalLocationLogger = createLogger('UniversalLocation');

/**
 * Universal Location Input Component
 * Supports address search, venue search, and combined search modes
 * Uses staff page layout (no toggles - search + manual fields always visible)
 */
const UniversalLocationInput: React.FC<UniversalLocationInputProps> = ({
  onLocationChange,
  initialValues = {},
  errors = {},
  onBlur,
  className = "",
  required = false,
  placeholder,
  disabled = false,
  showHeader = true
}) => {
  // Get app theme from context (not system preference)
  const { theme } = useTheme();
  
  // Ref for Google Maps integration
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);
  
  // Google Maps widget state (New Places API only)
  const [autocompleteWidget, setAutocompleteWidget] = useState<google.maps.places.PlaceAutocompleteElement | null>(null);
  
  // Location data state
  const [locationData, setLocationData] = useState<UniversalLocationData>({
    streetAddress: initialValues.streetAddress || '',
    addressLine2: initialValues.addressLine2 || '',
    city: initialValues.city || '',
    state: initialValues.state || '',
    zipCode: initialValues.zipCode || '',
    ...(initialValues.venueName && { venueName: initialValues.venueName })
  });
  
  // Search state
  const [searchState, setSearchState] = useState<LocationState>({
    searchValue: '',
    isSearching: false,
    hasSearchResults: false,
    showSelectedLocation: false
  });
  
  const [isLoadingScript, setIsLoadingScript] = useState(false);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(() => {
    // Initialize based on whether Google Maps is already loaded
    return isGoogleMapsApiLoaded() && !!window.google?.maps?.places;
  });
  
  // Get component configuration
  const componentConfig = getComponentConfig();
  const searchPlaceholder = placeholder || componentConfig.placeholder;
  
  universalLocationLogger.debug(`🎯 UniversalLocationInput initialized in combined mode`);

  // Sync with parent form values when they change
  useEffect(() => {
    const newData = {
      streetAddress: initialValues.streetAddress || '',
      addressLine2: initialValues.addressLine2 || '',
      city: initialValues.city || '',
      state: initialValues.state || '',
      zipCode: initialValues.zipCode || '',
      ...(initialValues.venueName && { venueName: initialValues.venueName })
    };
    
    setLocationData(newData);
    
    // Update search state if we have complete location data
    const isComplete = isLocationDataComplete(newData);
    setSearchState(prev => ({
      ...prev,
      showSelectedLocation: isComplete,
      hasSearchResults: isComplete
    }));
  }, [
    initialValues.streetAddress, 
    initialValues.addressLine2, 
    initialValues.city, 
    initialValues.state, 
    initialValues.zipCode,
    initialValues.venueName,
        ]);

  // Add CSS styling for Google Places web component
  useEffect(() => {
    const styleId = 'google-places-autocomplete-styling';
    
    // Check if style already exists
    if (document.getElementById(styleId)) {
      return;
    }
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Style the Google Places Autocomplete web component */
      /* Light theme (default) */
      gmp-place-autocomplete[data-theme="light"] {
        display: block !important;
        width: 100% !important;
        border: 1px solid rgb(209 213 219) !important; /* border-gray-300 */
        border-radius: 0.5rem !important; /* rounded-lg */
        box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important; /* shadow-sm */
        background-color: white !important;
        min-height: 44px !important;
        color-scheme: light !important;
        --gmpx-color-on-surface: rgb(31 41 55) !important; /* text-gray-800 */
        --gmpx-color-on-surface-variant: rgb(107 114 128) !important; /* placeholder text-gray-500 */
        --gmpx-color-surface: white !important;
      }
      
      /* Dark theme */
      gmp-place-autocomplete[data-theme="dark"] {
        display: block !important;
        width: 100% !important;
        border: 1px solid rgb(55 65 81) !important; /* border-gray-700 */
        border-radius: 0.5rem !important; /* rounded-lg */
        box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important; /* shadow-sm */
        background-color: rgb(17 24 39) !important; /* bg-gray-900 */
        min-height: 44px !important;
        color-scheme: dark !important;
        --gmpx-color-on-surface: rgb(255 255 255 / 0.9) !important; /* text-white/90 */
        --gmpx-color-on-surface-variant: rgb(255 255 255 / 0.5) !important; /* placeholder text-white/50 */
        --gmpx-color-surface: rgb(17 24 39) !important; /* bg-gray-900 */
      }
      
      /* Target the internal input for dark mode text color */
      gmp-place-autocomplete[data-theme="dark"] input {
        color: rgb(255 255 255 / 0.9) !important;
        background-color: rgb(17 24 39) !important;
      }
      
      /* Focus state - uses CheerVue brand theme colors */
      gmp-place-autocomplete:focus-within {
        border-color: var(--color-brand-500) !important;
        outline: none !important;
        box-shadow: 0 0 0 3px rgba(70, 95, 255, 0.2) !important;
      }
      
      gmp-place-autocomplete[data-theme="dark"]:focus-within {
        border-color: var(--color-brand-400) !important;
        box-shadow: 0 0 0 3px rgba(117, 146, 255, 0.2) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      // Cleanup: remove style when component unmounts
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);
  
  // Apply theme to Google Places widget when theme changes
  useEffect(() => {
    if (autocompleteContainerRef.current) {
      const widget = autocompleteContainerRef.current.querySelector('gmp-place-autocomplete');
      if (widget) {
        // Set data attribute for CSS targeting
        widget.setAttribute('data-theme', theme);
        // Also set color-scheme directly on the element
        (widget as HTMLElement).style.colorScheme = theme;
        universalLocationLogger.debug(`🎨 Applied ${theme} theme to Google Places widget`);
      }
    }
  }, [theme]);

  // Initialize Google Places Autocomplete with migration support
  useEffect(() => {
    if (disabled) return;
    
    const initializeGoogleMaps = async () => {
      if (!isGoogleMapsApiLoaded()) {
        setIsLoadingScript(true);
        setIsGoogleMapsReady(false);
        try {
          await loadGoogleMapsApiWithMonitoring();
          setIsLoadingScript(false);
          // Add a small delay to ensure Google Maps objects are fully available
          setTimeout(() => {
            initAutocomplete(0);
          }, 100);
        } catch (error) {
          universalLocationLogger.error('Failed to load Google Maps API:', error);
          setIsLoadingScript(false);
          setIsGoogleMapsReady(false);
        }
      } else {
        setIsGoogleMapsReady(true);
        // Add a small delay even when already loaded to ensure objects are available
        setTimeout(() => {
          initAutocomplete(0);
        }, 50);
      }
    };
    
    initializeGoogleMaps();
      }, [disabled]);
  
  // Initialize Google Places API (New) autocomplete widget with retry logic
  const initAutocomplete = useCallback((retryCount = 0) => {
    universalLocationLogger.debug('🔍 initAutocomplete called', {
      attempt: retryCount + 1,
      hasContainer: !!autocompleteContainerRef.current,
      hasGoogle: !!window.google,
      hasMaps: !!window.google?.maps,
      hasPlaces: !!window.google?.maps?.places,
      hasPlaceAutocompleteElement: !!window.google?.maps?.places?.PlaceAutocompleteElement
    });
    
    if (!autocompleteContainerRef.current || !window.google?.maps?.places) {
      if (retryCount < 3) {
        universalLocationLogger.warn(`⚠️ Retrying autocomplete initialization (attempt ${retryCount + 1}/3)`);
        setTimeout(() => initAutocomplete(retryCount + 1), 200);
        return;
      } else {
        universalLocationLogger.error('❌ Failed to initialize autocomplete after 3 attempts');
        return;
      }
    }
    
    const options: PlacesAutocompleteOptions = {
              types: componentConfig.searchTypes,
      componentRestrictions: { country: 'us' },
      fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id', 'types']
    };
    
    try {
      const widget = setupPlacesWidgetInContainer(
        autocompleteContainerRef.current,
        options
      );
      
      setAutocompleteWidget(widget);
      
      // Apply theme to the widget immediately after creation
      widget.setAttribute('data-theme', theme);
      (widget as unknown as HTMLElement).style.colorScheme = theme;
      
      // Setup event handling for Google Places API widget
      setupNewPlacesEventHandler(
        widget,
        handlePlaceSelect
      );
      
      universalLocationLogger.info(`✅ Google Places autocomplete widget initialized with ${theme} theme`);
      setIsGoogleMapsReady(true);
    } catch (error) {
      universalLocationLogger.error('Failed to initialize autocomplete:', error);
      setIsGoogleMapsReady(false);
    }
  }, [componentConfig.searchTypes, theme]);
  
  // Handle address selection from Google Places API
  const handlePlaceSelect = useCallback((place: PlaceData) => {
    if (!place) return;
    
    universalLocationLogger.debug('📍 Place selected:', place);
    setSearchState(prev => ({ ...prev, isSearching: true }));
    
    try {
      // Parse the place data into our standardized format
      const parsedData = parseGooglePlaceData(place);
      
      if (Object.keys(parsedData).length === 0) {
        universalLocationLogger.warn('⚠️ No usable data extracted from place');
        setSearchState(prev => ({ ...prev, isSearching: false }));
        return;
      }
      
      // Merge with existing data, preserving manual edits
      const updatedData = mergeLocationData(locationData, parsedData, ['addressLine2']);
      
      universalLocationLogger.debug(`🔍 Final location data - city: "${updatedData.city}"`);
      
      setLocationData(updatedData);
      setSearchState({
        searchValue: place.name || place.formatted_address || '',
        isSearching: false,
        hasSearchResults: true,
        showSelectedLocation: true
      });
      
      // Notify parent component
      onLocationChange(updatedData);
      
      // Note: New Google Places API handles input clearing automatically
      
      universalLocationLogger.info('✅ Location data updated:', updatedData);
    } catch (error) {
      universalLocationLogger.error('❌ Error processing place selection:', error);
      setSearchState(prev => ({ ...prev, isSearching: false }));
    }
  }, [locationData, onLocationChange, autocompleteWidget]);
  
  // Handle manual field changes
  const handleFieldChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Special handling for ZIP code formatting
    let processedValue = value;
    if (name === 'zipCode') {
      // Allow only digits and hyphens for ZIP codes
      processedValue = value.replace(/[^\d-]/g, '');
      // Limit to standard ZIP format length
      if (processedValue.length > 10) {
        processedValue = processedValue.slice(0, 10);
      }
    }
    
    const updatedData = { ...locationData, [name]: processedValue };
    setLocationData(updatedData);
    onLocationChange(updatedData);
    
    // Update search state if manual edits are made
    if (searchState.showSelectedLocation) {
      setSearchState(prev => ({
        ...prev,
        showSelectedLocation: isLocationDataComplete(updatedData)
      }));
    }
  }, [locationData, onLocationChange, searchState.showSelectedLocation]);
  
  // Handle state select change
  const handleStateChange = useCallback((value: string) => {
    const updatedData = { ...locationData, state: value };
    setLocationData(updatedData);
    onLocationChange(updatedData);
    
    // Update search state if manual edits are made
    if (searchState.showSelectedLocation) {
      setSearchState(prev => ({
        ...prev,
        showSelectedLocation: isLocationDataComplete(updatedData)
      }));
    }
  }, [locationData, onLocationChange, searchState.showSelectedLocation]);

  // Handle clearing the selected location
  const handleClearLocation = useCallback(() => {
    universalLocationLogger.debug('🗑️ Clearing selected location');
    
    // Reset location data
    const emptyData: UniversalLocationData = {
      streetAddress: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      ...(componentConfig.showVenueName && { venueName: '' })
    };
    
    setLocationData(emptyData);
    setSearchState({
      searchValue: '',
      isSearching: false,
      hasSearchResults: false,
      showSelectedLocation: false
    });
    
    // Notify parent component
    onLocationChange(emptyData);
    
    // Clear the Google Places widget input and re-initialize
    if (autocompleteContainerRef.current) {
      try {
        // Clear any existing input
        const input = autocompleteContainerRef.current.querySelector('input');
        if (input) {
          input.value = '';
        }
        
        // Remove existing widget if present
        if (autocompleteWidget) {
          autocompleteContainerRef.current.innerHTML = '';
          setAutocompleteWidget(null);
        }
        
        // Re-initialize the widget to ensure it's ready for new input
        if (isGoogleMapsReady && window.google?.maps?.places) {
          setTimeout(() => {
            initAutocomplete(0);
          }, 100);
        }
      } catch (error) {
        universalLocationLogger.warn('Error clearing widget:', error);
      }
    }
    
    universalLocationLogger.info('✅ Location cleared');
  }, [autocompleteWidget, componentConfig.showVenueName, onLocationChange, isGoogleMapsReady]);

  // Content to render (same whether wrapped in card or not)
  const content = (
    <>
      {/* Google Places Search Section */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg mb-4">
        <div className="mb-4">
          <Label>{componentConfig.description}</Label>
          {/* Loading state display */}
          {(isLoadingScript && !isGoogleMapsReady) && (
            <div className="flex items-center py-2 mb-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-3" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Loading search...</span>
            </div>
          )}
          {/* Google Places API widget container - Always visible */}
          <div ref={autocompleteContainerRef} className="min-h-[44px]">
            {/* Google widget will be inserted here */}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {componentConfig.description} or enter details manually below
          </p>
        </div>
        
        {/* Display selected location with clear button */}
        {searchState.showSelectedLocation && (
          <div className="mt-4 p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Selected Location
              </h4>
              <div className="space-y-1">
                {componentConfig.showVenueName && locationData.venueName && (
                  <p className="text-sm font-medium text-brand-600 dark:text-brand-400">
                    {locationData.venueName}
                  </p>
                )}
                <p className="text-sm text-gray-700 dark:text-gray-300">{formatLocationDisplay(locationData)}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearLocation}
              className="ml-3 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors flex-shrink-0"
              title="Clear selected location"
              aria-label="Clear selected location"
            >
              <GoogleMaterialIcon name="close" size="sm" />
            </button>
          </div>
        )}
      </div>
      
      {/* Manual Entry Fields - Always Visible (Staff Page Layout) */}
      <div className="space-y-4">
        <Typography variant="h6" className="text-gray-700 dark:text-gray-300 mb-3">
          Address Details
        </Typography>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Venue Name Field (always shown in combined mode) */}
      {componentConfig.showVenueName && (
            <div className="md:col-span-2">
              <Label htmlFor="venueName">
                Location or Custom Name
              </Label>
              <TextField
                id="venueName"
                name="venueName"
                type="text"
                placeholder="Enter location or custom name"
                value={locationData.venueName || ''}
                onChange={handleFieldChange}
                onBlur={onBlur}
                error={!!errors.venueName}
                hint={errors.venueName}
                disabled={disabled}
              />
            </div>
          )}
          
          {/* Street Address Field */}
          <div className="md:col-span-2">
            <Label htmlFor="streetAddress" required={required}>
              Street Address
            </Label>
            <TextField
              id="streetAddress"
              name="streetAddress"
              type="text"
              placeholder="123 Main St"
              value={locationData.streetAddress}
              onChange={handleFieldChange}
              onBlur={onBlur}
              error={!!errors.streetAddress}
              hint={errors.streetAddress}
              required={required}
              disabled={disabled}
            />
          </div>
          
          {/* Address Line 2 Field */}
          <div className="md:col-span-2">
            <Label htmlFor="addressLine2">
              Suite/Unit Number
            </Label>
            <TextField
              id="addressLine2"
              name="addressLine2"
              type="text"
              placeholder="Suite 101, Unit B, etc."
              value={locationData.addressLine2 || ''}
              onChange={handleFieldChange}
              onBlur={onBlur}
              error={!!errors.addressLine2}
              hint={errors.addressLine2}
              disabled={disabled}
            />
          </div>
          
          {/* City Field */}
          <div>
            <Label htmlFor="city" required={required}>
              City
            </Label>
            <TextField
              id="city"
              name="city"
              type="text"
              placeholder="Enter city"
              value={locationData.city}
              onChange={handleFieldChange}
              onBlur={onBlur}
              error={!!errors.city}
              hint={errors.city}
              required={required}
              disabled={disabled}
            />
          </div>
          
          {/* State Field */}
          <div>
            <Label id="state-label" htmlFor="state" required={required}>
              State
            </Label>
            <Dropdown
              id="state"
              ariaLabelledBy="state-label"
              name="state"
              options={US_STATES}
              value={locationData.state}
              onChange={handleStateChange}
              error={errors.state}
              required={required}
              disabled={disabled}
            />
          </div>
          
          {/* ZIP Code Field */}
          <div>
            <Label htmlFor="zipCode" required={required}>
              ZIP Code
            </Label>
            <TextField
              id="zipCode"
              name="zipCode"
              type="text"
              placeholder="12345"
              value={locationData.zipCode}
              onChange={handleFieldChange}
              onBlur={onBlur}
              error={!!errors.zipCode}
              hint={errors.zipCode}
              required={required}
              disabled={disabled}
              maxLength={10}
              inputMode="numeric"
            />
          </div>
        </div>
      </div>
    </>
  );

  // If showHeader is true, wrap in Card for standalone usage
  // If showHeader is false, it's being used inside another card, so don't wrap
  if (showHeader) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center">
            <GoogleMaterialIcon 
              name="map" 
              className="mr-2 text-gray-500 dark:text-gray-400" 
            />
            Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          {content}
        </CardContent>
      </Card>
    );
  }

  // When used inside another card, just return the content
  return (
    <div className={className}>
      {content}
    </div>
  );
};

export default UniversalLocationInput;

// Re-export types for consumers
export type { UniversalLocationData, UniversalLocationInputProps } from './types';